// Centralized theme configuration for Ant Design
import { colors } from './tokens/colors';
import { shadows } from './tokens/shadows';
import { radius } from './tokens/radius';
import { motion } from './tokens/motion';
import { blur } from './tokens/blur';
import { sizes } from './tokens/sizes';
import { breakpoints } from './tokens/breakpoints';
import { letterSpacing } from './tokens/letterSpacing';
import { spacing } from './tokens/spacing';
import { states } from './tokens/states';
import { typography } from './tokens/typography';
import type { ThemeConfig } from 'antd';

const theme: ThemeConfig = {
  token: {
    // Typography - Font Family
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', sans-serif",

    // Font Weights
    // Ant v6 built-in strong weight (used by headings + some selected states like Tabs)
    fontWeightStrong: 700,

    // Base colors - Figma tokens mapped to Ant Design naming
    colorWhite: colors.white,
    // colorBlack: colors.black,

    // Primary/Accent colors
    colorPrimary: colors.accentBlue,    // Figma: accent-blue
    colorSuccess: colors.green2,        // Figma: green.Green 2
    colorWarning: colors.accentYellow,  // Figma: accent-yellow
    colorError: '#ff4d4f',              // Ant Design default red

    // Background colors
    colorBgBase: colors.black,          // Pure black base
    colorBgContainer: colors.uiGrey2,   // Figma: ui-grey-2 (modal-background)
    colorBgElevated: colors.uiGrey4,    // Figma: ui-grey-4 (modal-button)
    colorBgLayout: colors.black,        // Pure black layout
    colorBgSpotlight: 'rgba(17, 17, 17, 0.6)', // Backdrop overlay

    // Text colors
    colorText: colors.white,                    // Figma: white
    colorTextSecondary: colors.lightGrey,       // Figma: light-grey
    colorTextTertiary: colors.translucentLightGrey, // Empty states / subtle text
    colorTextDisabled: colors.lightGrey,        // Disabled text (no dimming/opacity)
    colorTextPlaceholder: colors.lightGrey,     // Placeholder text

    // Icons (Ant internal icons like close/arrow/etc.). Heroicons typically inherit `currentColor`,
    // so this mainly affects Ant's built-in icon components.
    colorIcon: colors.white,
    colorIconHover: colors.white,

    // Border colors
    colorBorder: 'rgba(255, 255, 255, 0.12)',  // Resting border (grey)
    colorBorderSecondary: colors.darkGrey, // Figma: dark-grey
    colorBgContainerDisabled: colors.uiGrey1, // Disabled control bg (keep ui-grey-1)

    // Focus ring
    controlOutlineWidth: 1,
    controlOutline: '#0A84FF', // Focus ring color (non-Figma)

    // Disabled state (optional token reference)
    colorErrorOutline: 'rgba(255, 77, 79, 0.6)', // red-2 - Disabled outline



    // Border radius - Figma tokens mapped to Ant Design naming
    borderRadiusXS: radius.s,      // Figma: radius-s (Ant Design convention)
    borderRadiusSM: radius.s,      // Figma: radius-s
    borderRadius: radius.m,        // Figma: radius-m
    borderRadiusLG: radius.l,      // Figma: radius-l

    // Fully rounded (radius-round) lives in tokens/radius

    // Typography - Figma tokens mapped to Ant Design naming
    // Headings
    fontSizeHeading1: typography.fontSizeHeading1,
    // Ant expects lineHeight tokens as unitless multipliers, not px.
    lineHeightHeading1: typography.lineHeightHeading1 / typography.fontSizeHeading1,

    fontSizeHeading2: typography.fontSizeHeading2,
    lineHeightHeading2: typography.lineHeightHeading2 / typography.fontSizeHeading2,

    fontSizeHeading3: typography.fontSizeHeading3,
    lineHeightHeading3: typography.lineHeightHeading3 / typography.fontSizeHeading3,

    fontSizeHeading4: typography.fontSizeHeading4,
    lineHeightHeading4: typography.lineHeightHeading4 / typography.fontSizeHeading4,

    fontSizeHeading5: typography.fontSizeHeading5,
    lineHeightHeading5: typography.lineHeightHeading5 / typography.fontSizeHeading5,

    // Body text
    fontSize: typography.fontSize,
    lineHeight: typography.lineHeight / typography.fontSize,

    fontSizeLG: typography.fontSizeLG,
    lineHeightLG: typography.lineHeightLG / typography.fontSizeLG,

    fontSizeSM: typography.fontSizeSM,
    lineHeightSM: typography.lineHeightSM / typography.fontSizeSM,

    // Letter spacing (tracking) - Figma tokens

    // Spacing - Figma tokens mapped to Ant Design naming
    paddingXXS: spacing.px4,  // Figma: space-1
    paddingXS: spacing.px8,   // Figma: space-2
    paddingSM: spacing.px12,  // Figma: space-3
    padding: spacing.px16,    // Figma: space-4 (base)
    paddingLG: spacing.px20,  // Figma: space-5
    paddingXL: spacing.px24,  // Figma: space-6

    marginXXS: spacing.px4,  // Figma: space-1
    marginXS: spacing.px8,   // Figma: space-2
    marginSM: spacing.px12,  // Figma: space-3
    margin: spacing.px16,    // Figma: space-4 (base)
    marginLG: spacing.px20,  // Figma: space-5
    marginXL: spacing.px24,  // Figma: space-6
    marginXXL: spacing.px32, // Figma: space-8

    // Extended spacing (larger values without Ant Design equivalents)

    // Component specific
    // Global control height defaults (fallbacks for components without overrides)
    controlHeight: sizes.controlHeight.md,
    controlHeightLG: sizes.controlHeight.lg,

    // Shadows - mapped from Figma shadow tokens
    boxShadowSecondary: shadows.m3Dark1,  // Small cards / subtle elevation
    boxShadow: shadows.m3Dark3,            // Normal elevation for surfaces

    // Custom design tokens (Ant Design doesn't have native equivalents)
    // Blur effects

    // Breakpoints - Figma tokens mapped to Ant Design screen naming
    screenSM: breakpoints.sm,   // Figma: breakpoint-s
    screenMD: breakpoints.md,   // Figma: breakpoint-m
    screenLG: breakpoints.lg,   // Figma: breakpoint-l
    screenXL: breakpoints.xl,   // Figma: breakpoint-xl

    // Motion - Figma tokens mapped to Ant Design naming
    // Duration (in milliseconds)
    motionDurationFast: `${motion.durationFast}ms`, // Figma: duration-fast
    motionDurationMid: `${motion.durationFast}ms`,   // Figma: duration-normal
    motionDurationSlow: `${motion.durationSlow}ms`, // Figma: duration-slow

    // Easing functions
    motionEaseInOut: motion.easeInOut, // Figma: easing-standard
    motionEaseOut: motion.easeOut,     // Figma: easing-decelerate

    // Global z-index for popups (Drawers, Modals, etc.)
    // Sider is at 999, backdrop overlay is at 998.
    zIndexPopupBase: 900,
  },

  // Component-specific theme overrides
  components: {
    // App shell
    Layout: {
      headerBg: '#000',
      bodyBg: '#000',
      siderBg: '#000',
      headerHeight: 60,
      headerPadding: 0,
    },

    Menu: {
      darkItemBg: '#000',
      darkSubMenuItemBg: '#000',
      darkItemSelectedBg: '#000',
      darkItemColor: '#FFF',  // Menu text color in dark mode
      darkItemHoverColor: '#FFF',  // Menu text hover color in dark mode
      darkItemHoverBg: 'transparent', // Hypothesis G: Force transparent hover bg at theme level
      itemHeight: 46,
      fontSize: typography.fontSizeHeading3,
      iconSize: sizes.icon.m,
      motion: false, // Hypothesis F: Disable motion at theme level
    },

    // 2. Buttons
    Button: {
      // Heights from Figma
      controlHeightSM: sizes.controlHeight.sm,   // small (View All)
      controlHeight: sizes.controlHeight.md,     // middle (tab-sized)
      controlHeightLG: sizes.controlHeight.lg,   // large (primary)

      // Pill shape
      borderRadius: radius.round,
      borderRadiusSM: radius.round,
      borderRadiusLG: radius.round,

      // Typography - Button uses "contentFontSize" prefix
      contentFontSizeSM: typography.fontSizeHeading6, // H6 (10 / 14) - Small buttons
      contentFontSize: typography.fontSizeHeading3,   // H3 (16 / 22) - Default buttons
      contentFontSizeLG: typography.fontSizeHeading3, // Large buttons

      // Icon gap (v6 token) — replaces CSS var override
      iconGap: spacing.px4,

      // Padding - Button uses "paddingInline" prefix
      paddingInlineSM: spacing.px8,
      paddingInline: spacing.px24,
      paddingInlineLG: spacing.px24,

      fontWeight: 600,       // default buttons

      // Primary button colors - white bg, black text
      primaryColor: colors.black,   // Black text
      colorPrimary: colors.white,   // White background (overrides global colorPrimary)
      colorPrimaryHover: colors.white, // Keep white on hover (avoid global colorPrimaryHover)
      colorPrimaryActive: colors.white,
      // No border on primary in any state (v6 token keys)
      colorPrimaryBorder: 'transparent',
      colorPrimaryBorderHover: 'transparent',

      // Disabled text color - light-grey token
      colorTextDisabled: colors.lightGrey, // Light-grey (no translucency)

      // Primary button should NOT have elevation. `primaryShadow` in Ant v6 is the
      // box-shadow applied to solid buttons, not just a focus ring.
      primaryShadow: 'none',

      // Color overrides (keep existing)
      defaultBg: colors.uiGrey1, // ui-grey-1 (platform-background)
      defaultColor: colors.white,
      defaultBorderColor: 'transparent', // No border on secondary buttons
      defaultHoverBg: colors.uiGrey1, // Stay ui-grey-1 on hover (no change)
      defaultHoverColor: colors.white, // White text on hover (not blue)
      // NOTE (Ant v6): outlined default button uses *border* hover token for *text* hover color
      // Keep this white for text, and keep border hidden via minimal CSS in theme/global.css.
      defaultHoverBorderColor: colors.white,
      defaultActiveBg: colors.uiGrey1, // Stay ui-grey-1 on active (no change)
      defaultActiveColor: colors.white, // Keep white text
      // Same coupling as hover: keep white for text.
      defaultActiveBorderColor: colors.white,
      // Disabled (v6 token keys)
      defaultBgDisabled: colors.uiGrey1,
      dashedBgDisabled: colors.uiGrey1,
    },

    // 3. Tabs
    Tabs: {
      // Tabs label typography should match H3 (16 / 22, bold)
      // Note: Tabs does not expose a fontWeight token in v6; weight is driven by `token.fontWeightStrong`.
      titleFontSize: typography.fontSizeHeading3,
      titleFontSizeLG: typography.fontSizeHeading2,
      titleFontSizeSM: typography.fontSizeHeading6,

      // Remove the Tabs baseline border (Ant uses `colorBorderSecondary` for the nav `::before` line)
      colorBorderSecondary: 'transparent',

      // Hide ink bar for pill-style tabs
      inkBarColor: 'transparent',

      // Text colors for pill tabs
      itemColor: colors.lightGrey,           // Inactive tab text
      itemHoverColor: colors.lightGrey,      // Hover tab text (stays same)
      itemActiveColor: colors.lightGrey,     // Active/clicking tab text (stays light grey)
      itemSelectedColor: colors.black,       // Selected tab text (BLACK for white bg)

      // Spacing for pill tabs
      horizontalItemGutter: spacing.px12,    // Gap between pills
      horizontalMargin: '0',                 // Remove default margins

      // Note: Background colors and padding are handled via CSS in DirectorySearch.css
      // as these tokens are not available in this Ant Design version
    },

    // 4. Inputs
    Input: {
      // height ladder
      controlHeightSM: sizes.controlHeight.sm,
      controlHeight: sizes.controlHeight.md,
      controlHeightLG: sizes.controlHeight.lg,

      // pill shape (matches buttons)
      borderRadius: radius.round,
      borderRadiusSM: radius.round,
      borderRadiusLG: radius.round,

      // text sizing (Ant v6 Input uses `inputFontSize*` tokens, not `fontSize*`)
      inputFontSizeSM: typography.fontSizeHeading6,
      inputFontSize: typography.fontSizeHeading4,
      inputFontSizeLG: typography.fontSizeHeading4,

      // padding (tuned for pill controls)
      paddingInline: spacing.px16,
      paddingInlineSM: spacing.px12,
      paddingInlineLG: 18,
    },

    // 5. Selects / Dropdowns
    Select: {
      // height ladder
      controlHeightSM: sizes.controlHeight.sm,
      controlHeight: sizes.controlHeight.md,
      controlHeightLG: sizes.controlHeight.lg,

      // pill shape
      borderRadius: radius.round,

      // text sizing
      fontSizeSM: 10,
      fontSize: 16,
      fontSizeLG: 16,

      // padding

      // Dropdown option states (fix: default Ant tokens can produce light selected backgrounds on dark UI)
      optionSelectedBg: colors.uiGrey4,
      optionSelectedColor: colors.white,
      optionActiveBg: colors.uiGrey2,
    },

    // 6. Table
    Table: {
      headerBg: '#000',
      headerColor: '#fff',
      rowHoverBg: 'rgba(255, 255, 255, 0.08)',
      // Keep selected rows dark (no white background) when using rowSelection checkboxes
      rowSelectedBg: 'transparent',
      rowSelectedHoverBg: 'rgba(255, 255, 255, 0.08)',
      borderColor: '#333',
      bodySortBg: 'rgba(255, 255, 255, 0.04)',
      rowExpandedBg: '#111',
      colorBgContainer: '#000',
      colorText: '#fff',
      colorTextHeading: '#fff',
    },

    // 9. Overlays (Modal)
    Modal: {
      // Corner radius (Modal container uses `token.borderRadiusLG`)
      borderRadiusLG: radius.xxl,

      // Title typography (H2: 20 / 28)
      titleFontSize: typography.fontSizeHeading2,
      // Ant expects unitless multiplier
      titleLineHeight: typography.lineHeightHeading2 / typography.fontSizeHeading2,

      // Body typography (Body Large: 14 / 20)
      fontSize: typography.fontSizeLG,
      lineHeight: typography.lineHeightLG / typography.fontSizeLG,
      boxShadow: shadows.specDark4,
    },

    // 12. Alerts
    // Alerts: set default message text to Body Large (14/20) without affecting global typography
    Alert: {
      fontSize: typography.fontSizeLG,
      // Ant expects unitless multiplier
      lineHeight: typography.lineHeightLG / typography.fontSizeLG,
      defaultPadding: '16px',

      // Keep alert icons white even for semantic types (info/success/warning/error)
      // without changing global semantic colors for other components.
      colorInfo: colors.white,
      colorSuccess: colors.white,
      colorWarning: colors.white,
      colorError: colors.white,
    },

    // 13. Drawer
    Drawer: {
      colorBgElevated: colors.black,
      colorBgMask: 'rgba(17, 17, 17, 0.6)',
      borderRadiusLG: 0, // Drawers usually go edge-to-edge
      boxShadow: shadows.specDark4,
    },

    // 14. Avatar
    Avatar: {
      containerSizeSM: 16, // Small avatar size
    },

    // 15. DatePicker / TimePicker
    DatePicker: {
      // Control styling
      controlHeight: sizes.controlHeight.md,
      controlHeightLG: sizes.controlHeight.lg,
      borderRadius: radius.round,

      // Dropdown panel
      cellActiveWithRangeBg: colors.uiGrey4,
      cellHoverBg: colors.uiGrey2,
      cellBgDisabled: colors.uiGrey1,
    },
  },
};

export default theme;
